import _ from 'underscore';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useContext, useRef } from 'react';
import {
  AdsCopilot,
  ActivityRendererManager
} from '@bingads-webui/ads-copilot-ui';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { usePilotsValue } from '@bingads-webui-campaign-react/basic-context-utils';
import { withLiveAgent, useLiveAgentTransfer, LiveAgentConnectionState, CaptchaVerification, LiveAgentOnlyModeHeader, I18nProvider } from '@bingads-webui/copilot-live-agent';
import { CopilotResizer } from '@bingads-webui-react/copilot-resizer';
import { makeStyles, tokens } from '@fluentui/react-components';

import { renderers, ReconnectCard } from './components/index';
import { createLiveAgentCustomContext, LIVE_AGENT_ONLY_MODE_CONNECTION_DESCRIPTION } from './utils';

const AdsCopilotWithLiveAgent = withLiveAgent(AdsCopilot);

const useCaptchaContainerStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground5,
  },
  captchaContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: tokens.spacingVerticalXXL,
    backgroundColor: tokens.colorNeutralBackground2,
    flexGrow: 1,
  },
});

export const AdsCopilotLoader = React.memo((props) => {
  const {
    isLiveAgentOnlyMode,
    onClose,
    restartLiveAgentConversation,
  } = props;

  const instrumentation = useContext(InstrumentationContext);
  const serverContext = useContext(ServerContext);
  // Signup flow opts in to CAPTCHA verification before live agent connection.
  // The pilot controls gradual rollout — when pilot is off, CAPTCHA is skipped.
  const isCaptchaEnabled = usePilotsValue('IsPMaxLiteCopilotLiveAgentCaptchaEnabled');

  const email = serverContext?.Identity?.Email;
  const firstName = serverContext?.Identity?.FirstName || serverContext?.Identity?.UserName;
  const lastName = serverContext?.Identity?.LastName;

  const uiActivity = useMemo(() => {
    if (instrumentation && instrumentation.pmaxLiteSignup) {
      return instrumentation.pmaxLiteSignup.create().copilotLiveAgent.create();
    }
    return { trace: _.noop, error: _.noop };
  }, [instrumentation]);

  uiActivity?.trace('[PmaxLiteSignup][AdsCopilotLoader] Copilot is initialized');

  const captchaContainerStyles = useCaptchaContainerStyles();

  const i18n = useContext(I18nContext);

  const locale = useMemo(() => i18n.getLocale(), [i18n]);

  const shouldResumeConversation = useRef(false);

  const liveAgentCustomContext = useMemo(
    () =>
      createLiveAgentCustomContext({
        email,
        firstName,
        lastName,
        locale,
      }),
    [email, firstName, lastName, locale]
  );

  const liveAgentOnlyModeProblemDescription = useMemo(() => {
    if (isLiveAgentOnlyMode) {
      return i18n.getString(LIVE_AGENT_ONLY_MODE_CONNECTION_DESCRIPTION);
    }
    return undefined;
  }, [isLiveAgentOnlyMode, i18n]);

  const {
    liveAgentState,
    liveAgentStream,
    isLiveAgentTyping,
    startLiveAgentChat,
    closeLiveAgentChat,
    isCaptchaVerified,
    onCaptchaVerified,
  } = useLiveAgentTransfer({
    username: '',
    accountId: '1234',
    allowFetchTokenViaApi: false,
    isLiveAgentOnlyMode,
    locale,
    shouldResumeConversation,
    customContext: liveAgentCustomContext,
    liveAgentOnlyModeProblemDescription,
    isCaptchaRequired: isCaptchaEnabled,
  });

  const liveAgentActivityProps = useMemo(() => ({
    isAIDegraded: false,
    locale,
    onLiveAgentTokensReceive: _.noop,
    onLiveAgentTransferClick: startLiveAgentChat,
    isCaptchaEnabled,
  }), [locale, startLiveAgentChat, isCaptchaEnabled]);

  const handleEndLiveAgentChatClick = useCallback(() => {
    uiActivity?.trace('[PmaxLiteSignup][AdsCopilotLoader] end live agent chat button clicked');
    closeLiveAgentChat(true);
  }, [closeLiveAgentChat, uiActivity]);

  const onCopilotClose = useCallback(() => {
    uiActivity?.trace('[PmaxLiteSignup][AdsCopilotLoader] Copilot panel is closed');
    onClose(isCaptchaVerified);
  }, [onClose, uiActivity, isCaptchaVerified]);

  const botServiceConfig = useMemo(() => ({
    token: '',
    username: 'pmax-lite-signup',
  }), []);

  const renderActivity = useCallback((activity, params, defaultRenderer) => (
    <ActivityRendererManager
      key={params.key}
      activity={activity}
      defaultRenderer={defaultRenderer}
      params={params}
      liveAgentActivityProps={liveAgentActivityProps}
      renderers={renderers}
      isInlineLiveAgentButtonEnabled
    />
  ), [liveAgentActivityProps]);

  const renderBottomComponent = useCallback(() => {
    if (liveAgentState === LiveAgentConnectionState.DISCONNECTED) {
      return <ReconnectCard onClick={restartLiveAgentConversation} />;
    }
    return null;
  }, [liveAgentState, restartLiveAgentConversation]);

  useEffect(() => {
    uiActivity?.trace('[PmaxLiteSignup][AdsCopilotLoader] Copilot is Rendered');
  }, [uiActivity]);

  if (!isCaptchaVerified) {
    return (
      <I18nProvider bingAdsI18n={i18n} locale={locale}>
        <CopilotResizer>
          <div className={captchaContainerStyles.root}>
            <LiveAgentOnlyModeHeader
              headerText={_TL_('Microsoft Advertising Support')}
              onClose={() => onClose(false)}
            />
            <div className={captchaContainerStyles.captchaContent}>
              <CaptchaVerification onVerified={onCaptchaVerified} logger={uiActivity} />
            </div>
          </div>
        </CopilotResizer>
      </I18nProvider>
    );
  }

  return (
    <AdsCopilotWithLiveAgent
      botServiceConfig={botServiceConfig}
      maxInputLength={500}
      locale={locale}
      isStopGeneratingEnabled
      renderActivity={renderActivity}
      currencyCode="USD"
      isCopilotDegraded={false}
      onClose={onCopilotClose}
      logger={uiActivity}
      bingAdsI18n={i18n}
      shouldResumeConversation={false}
      liveAgentState={liveAgentState}
      isLiveAgentTyping={isLiveAgentTyping}
      isLiveAgentOnlyMode={isLiveAgentOnlyMode}
      customStream={liveAgentStream}
      onEndLiveAgentChatClick={handleEndLiveAgentChatClick}
      renderBottomComponent={renderBottomComponent}
      showHeader
      isUxImprovementsEnabled
      enableResizer
    />
  );
});

AdsCopilotLoader.propTypes = {
  isLiveAgentOnlyMode: PropTypes.bool,
  onClose: PropTypes.func,
  restartLiveAgentConversation: PropTypes.func,
};

AdsCopilotLoader.defaultProps = {
  isLiveAgentOnlyMode: true,
  onClose: _.noop,
  restartLiveAgentConversation: _.noop,
};
